// Angular
import { Injectable } from '@angular/core';

// Third party
import { Observable } from 'rxjs';

// Local library
import {
  IBaseResponseSlim,
  ILoggedInUserModel,
  ILoginRequest,
  IResponseModel,
  ISignupRequest,
  ITransactionStatus,
  IUploadIdDocumentsModel,
  IVerifyDocRequest,
  IVerifyIdRequest,
  IVerifyIdentityRequest
} from '@amwager/util-ts';

// Local file
import { HttpService } from '../services';



// TODO: write tests
@Injectable()
export class UserAPIService {
  private readonly API_URL = '/api/User';

  constructor(
    private readonly httpService: HttpService,
  ) {}


  /**
   * GET API
   */
  guest$(): Observable<IResponseModel<ILoggedInUserModel>> {
    return this.httpService.get$({
      api: `${this.API_URL}/Guest`
    });
  }

  /**
   * @description
   * Gets a list of fields that are required for identification for a country.
   *
   * @param country string
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  idFields$(country: string): Observable<{}> {
    return this.httpService.get$({
      api: `${this.API_URL}/IdFields`,
      params: this.httpService.objToHttpParams({
        country
      })
    });
  }

  /**
   * @description
   * Gets a list of fields that are required for identification for a country.
   *
   * @param country string
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  idConsents$(country: string): Observable<{}> {
    return this.httpService.get$({
      api: `${this.API_URL}/IdConsents`,
      params: this.httpService.objToHttpParams({
        country
      })
    });
  }

  /**
   * @description
   * Returns promo code information if valid.
   *
   * @param promoCode string
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  checkPromoCode$(promoCode: string): Observable<{}> {
    return this.httpService.get$({
      api: `${this.API_URL}/CheckPromoCode`,
      params: this.httpService.objToHttpParams({
        promoCode
      })
    });
  }

  /**
   * @description
   * Second stage verification of self-exclusion or suspension.
   *
   * @param verificationToken string
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  selfExcludeOrSuspendVerification$(verificationToken: string): Observable<{}> {
    return this.httpService.get$({
      api: `${this.API_URL}/SelfExcludeOrSuspendVerification`,
      params: this.httpService.objToHttpParams({
        verificationToken
      })
    });
  }

  /**
   * @description
   * Get the properties of the AmWest customer account.
   *
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  account$(): Observable<{}> {
    return this.httpService.get$({
      api: `${this.API_URL}/Account`
    });
  }



  /**
   * POST API
   */

  /**
   * @description
   * Creates a new user account with limited permissions.
   *
   * @param signupRequest ISignupRequest
   * @returns Observable<IResponseModel<IBaseResponseSlim>>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  signup$(signupRequest: ISignupRequest): Observable<IResponseModel<IBaseResponseSlim>> {
    return this.httpService.post$({
      api: `${this.API_URL}/Signup`,
      body: signupRequest
    });
  }


  /**
   * @description
   * Verifies identity of the account holder and updates permissions if successful.
   *
   * @param verifyIdentity IVerifyIdentityRequest
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  verifiyIdentity$(verifyIdentity: IVerifyIdentityRequest)
    : Observable<IResponseModel<IBaseResponseSlim>> {
    return this.httpService.post$({
      api: `${this.API_URL}/VerifiyIdentity`,
      body: verifyIdentity
    });
  }

  /**
   * @description
   * Verifies ID of the account holder and updates permissions if successful.
   *
   * @param verifyIdentity IVerifyIdRequest
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  verifiyId$(verifyIdentity: IVerifyIdRequest): Observable<{}> {
    return this.httpService.post$({
      api: `${this.API_URL}/VerifiyId`,
      body: verifyIdentity
    });
  }

  /**
   * @description
   * Verifies ID of the account holder and updates permissions if successful.
   *
   * @param requests ITransactionStatus
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  verifyIdCallback$(requests: ITransactionStatus): Observable<{}> {
    return this.httpService.post$({
      api: `${this.API_URL}/VerifyIdCallback`,
      body: requests
    });
  }

  /**
   * @description
   * Verifies ID of the account holder and updates permissions if successful.
   *
   * @param verifyDocument IVerifyDocRequest
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  verifiyDocument$(verifyDocument: IVerifyDocRequest): Observable<{}> {
    return this.httpService.post$({
      api: `${this.API_URL}/VerifiyDocument`,
      body: verifyDocument
    });
  }

  /**
   * @description
   * Verifies ID of the account holder and updates permissions if successful.
   *
   * @param requests ITransactionStatus
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  verifyDocumentCallback$(requests: ITransactionStatus): Observable<{}> {
    return this.httpService.post$({
      api: `${this.API_URL}/VerifyDocumentCallback`,
      body: requests
    });
  }

  /**
   * @description
   * Login a user to the system.
   *
   * @param requests ILoginRequest
   * @returns Observable<IResponseModel<ILoggedInUserModel>>
   */
  login$(requests: ILoginRequest): Observable<IResponseModel<ILoggedInUserModel>> {
    return this.httpService.post$({
      api: `${this.API_URL}/Login`,
      body: requests
    });
  }

  /**
   *
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  refreshToken$(): Observable<{}> {
    return this.httpService.post$({
      api: `${this.API_URL}/RefreshToken`,
    });
  }

  /**
   *
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  verifyTPToken$(): Observable<{}> {
    return this.httpService.post$({
      api: `${this.API_URL}/VerifyTPToken`,
    });
  }

  /**
   * @description
   * Logout a user from the system
   *
   * @returns Observable<IBaseResponseSlim>
   */
  logout$(): Observable<IResponseModel<IBaseResponseSlim>> {
    return this.httpService.post$({
      api: `${this.API_URL}/Logout`
    });
  }

  /**
   * @description
   * Initiates a self-exclusion or suspension period for the account.
   *
   * @param years number
   * @param days number
   * @param isExclusion boolean
   * @returns Observable<{}>
   */
  selfExcludeOrSuspend$(years: number, days: number, isExclusion: boolean = true)
    // eslint-disable-next-line @typescript-eslint/ban-types
    : Observable<{}> {
    return this.httpService.post$({
      api: `${this.API_URL}/SelfExcludeOrSuspend`,
      body: {
        Years: years,
        Days: days,
        IsExclusion: isExclusion
      }
    });
  }

  // /**
  //  * @description
  //  * Initiates a self-exclusion or suspension period for the account.
  //  *
  //  * @param years number
  //  * @param days number
  //  * @param isExclusion boolean
  //  * @returns Observable<{}>
  //  */
  // selfExcludeOrSuspend$(years: number, days: number, isExclusion: boolean = true)
  //   // eslint-disable-next-line @typescript-eslint/ban-types
  //   : Observable<{}> {
  //   return this.httpService.post$({
  //     api: `${this.API_URL}/SelfExcludeOrSuspend`,
  //     params: this.httpService.objToHttpParams({
  //       years,
  //       days,
  //       isExclusion
  //     })
  //   });
  // }

  /**
   *
   * @param uploadDocuments IUploadIdDocumentsModel
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  uploadDocuments$(uploadDocuments: IUploadIdDocumentsModel): Observable<IResponseModel<IBaseResponseSlim>> {
    return this.httpService.post$({
      api: `${this.API_URL}/UploadDocuments`,
      body: uploadDocuments
    });
  }

}
